import { useState, useEffect } from 'react';

interface AssetResponse {
  nativeBalance: {
    lamports: number;
    price_per_sol: number;
    total_price: number;
  };
  items: Array<{
    id: string;
    interface: string;
    token_info?: {
      balance: number;
      supply: number;
      decimals: number;
      token_program: string;
      associated_token_address: string;
      price_info?: {
        total_price: number;
      };
    };
  }>;
}

interface Asset {
  id: string;
  interface: string;
  token_info?: {
    balance: number;
    supply: number;
    decimals: number;
    token_program: string;
    associated_token_address: string;
    price_info?: {
      total_price: number;
    };
  };
}

export function useWalletBalance() {
  const [solBalance, setSolBalance] = useState<number>(0);
  const [tokenBalance, setTokenBalance] = useState<number>(0);
  const [solUsdValue, setSolUsdValue] = useState<number>(0);
  const [tokenUsdValue, setTokenUsdValue] = useState<number>(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchBalance = async () => {
      try {
        const response = await fetch(import.meta.env.VITE_HELIUS_RPC_URL, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            jsonrpc: '2.0',
            id: 'my-id',
            method: 'getAssetsByOwner',
            params: {
              ownerAddress: import.meta.env.VITE_DEV_WALLET_PUBKEY,
              displayOptions: {
                showFungible: true,
                showNativeBalance: true,
              },
            },
          }),
        });

        const { result } = await response.json();
        
        // Get SOL balance and USD value
        const solBalanceAmount = result.nativeBalance.lamports / 1e9;
        const solUsdAmount = result.nativeBalance.total_price || 0;
        
        // Find our token in the items array and get its balance and USD value
        const tokenAsset = result.items.find((item: Asset) => item.id === import.meta.env.VITE_SPL_TOKEN_MINT);
        const tokenDecimals = tokenAsset?.token_info?.decimals || 9;
        const tokenAmount = tokenAsset?.token_info?.balance ? tokenAsset.token_info.balance / Math.pow(10, tokenDecimals) : 0;
        const tokenUsdAmount = tokenAsset?.token_info?.price_info?.total_price || 0;

        setSolBalance(solBalanceAmount);
        setTokenBalance(tokenAmount);
        setSolUsdValue(solUsdAmount);
        setTokenUsdValue(tokenUsdAmount);
        setLoading(false);
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to fetch balance');
        setLoading(false);
      }
    };

    fetchBalance();
  }, []);

  const totalUsdValue = solUsdValue + tokenUsdValue;

  return { solBalance, tokenBalance, solUsdValue, tokenUsdValue, totalUsdValue, loading, error };
} 